import React from 'react';
import { useTeamForm } from '../lib/useTeamForm';
import CustomInput from '../../../shared/ui/input-form/CustomInput';
import AdminPageLoader from '../../../shared/ui/admin-loader/AdminPageLoader';
import ImageUpload from '../../../shared/ui/image-upload/ImageUpload';
import { useAutoTranslate } from '../../../shared/lib/useAutoTranslate';
import TranslateButton from '../../../shared/ui/translate-button/TranslateButton';

const TeamForm = ({ type, initialData }) => {
    const formik = useTeamForm(type, initialData);
    const { translateFields, isTranslating } = useAutoTranslate(formik);

    const handleTranslate = async () => {
        await translateFields([
            { from: 'fullName.ua', to: 'fullName.en' },
            { from: 'role.ua', to: 'role.en' },
            { from: 'expertise.ua', to: 'expertise.en' },
            { from: 'bio.ua', to: 'bio.en' },
        ]);
    };

    return (
        <form id="team-create-form" onSubmit={formik.handleSubmit} className="team-form">
            {formik.isSubmitting && (
                <AdminPageLoader label="Збереження учасника..." fullScreen />
            )}

            {/* Translate Button */}
            <div className="form-group" style={{ marginBottom: '24px' }}>
                <TranslateButton
                    onClick={handleTranslate}
                    isLoading={isTranslating}
                    label="Перекласти УА → EN"
                />
            </div>

            {/* Фото учасника */}
            <ImageUpload
                value={formik.values.photoUrl}
                onChange={(url) => formik.setFieldValue('photoUrl', url)}
                label="Фото учасника"
                error={formik.errors.photoUrl}
                touched={formik.touched.photoUrl}
            />

            {/* Повне імʼя */}
            <div className="form-group">
                <label>Повне імʼя *</label>
                <div className="form-wrapper-2-column">
                    <CustomInput
                        id="fullName.ua" name="fullName.ua" label="Українська (UA)"
                        value={formik.values.fullName?.ua || ''}
                        onChange={(e) => formik.setFieldValue('fullName.ua', e.target.value)}
                        onBlur={formik.handleBlur}
                        placeholder="Наприклад: Світлана Петренко"
                        error={formik.errors.fullName?.ua}
                        touched={formik.touched.fullName?.ua}
                    />
                    <CustomInput
                        id="fullName.en" name="fullName.en" label="Англійська (EN)"
                        value={formik.values.fullName?.en || ''}
                        onChange={(e) => formik.setFieldValue('fullName.en', e.target.value)}
                        onBlur={formik.handleBlur}
                        placeholder="E.g. Svitlana Petrenko"
                    />
                </div>
            </div>

            {/* Посада */}
            <div className="form-group">
                <label>Посада *</label>
                <div className="form-wrapper-2-column">
                    <CustomInput
                        id="role.ua" name="role.ua" label="Українська (UA)"
                        value={formik.values.role?.ua || ''}
                        onChange={(e) => formik.setFieldValue('role.ua', e.target.value)}
                        onBlur={formik.handleBlur}
                        placeholder="Наприклад: Сертифікований викладач"
                        error={formik.errors.role?.ua}
                        touched={formik.touched.role?.ua}
                    />
                    <CustomInput
                        id="role.en" name="role.en" label="Англійська (EN)"
                        value={formik.values.role?.en || ''}
                        onChange={(e) => formik.setFieldValue('role.en', e.target.value)}
                        onBlur={formik.handleBlur}
                        placeholder="E.g. Certified Teacher"
                    />
                </div>
            </div>

            {/* Спеціальність */}
            <div className="form-group">
                <label>Спеціальність / Сертифікації</label>
                <div className="form-wrapper-2-column">
                    <CustomInput
                        id="expertise.ua" name="expertise.ua" label="Українська (UA)"
                        value={formik.values.expertise?.ua || ''}
                        onChange={(e) => formik.setFieldValue('expertise.ua', e.target.value)}
                        onBlur={formik.handleBlur}
                        placeholder="Наприклад: CELTA, General English"
                        error={formik.errors.expertise?.ua}
                        touched={formik.touched.expertise?.ua}
                    />
                    <CustomInput
                        id="expertise.en" name="expertise.en" label="Англійська (EN)"
                        value={formik.values.expertise?.en || ''}
                        onChange={(e) => formik.setFieldValue('expertise.en', e.target.value)}
                        onBlur={formik.handleBlur}
                        placeholder="E.g. CELTA, General English"
                    />
                </div>
            </div>

            {/* Біографія */}
            <div className="form-group">
                <label>Біографія</label>
                <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '12px', color: '#666' }}>Українська (UA)</label>
                    <textarea
                        id="bio.ua"
                        name="bio.ua"
                        value={formik.values.bio?.ua || ''}
                        onChange={(e) => formik.setFieldValue('bio.ua', e.target.value)}
                        onBlur={formik.handleBlur}
                        placeholder="Короткий опис учасника команди"
                        className={`custom-textarea ${formik.touched.bio?.ua && formik.errors.bio?.ua ? 'error' : ''}`}
                        rows="4"
                    />
                    {formik.touched.bio?.ua && formik.errors.bio?.ua && (
                        <span className="form-error">{formik.errors.bio?.ua}</span>
                    )}
                </div>
                <div>
                    <label style={{ fontSize: '12px', color: '#666' }}>Англійська (EN)</label>
                    <textarea
                        id="bio.en"
                        name="bio.en"
                        value={formik.values.bio?.en || ''}
                        onChange={(e) => formik.setFieldValue('bio.en', e.target.value)}
                        onBlur={formik.handleBlur}
                        placeholder="Short team member description"
                        className="custom-textarea"
                        rows="4"
                    />
                </div>
            </div>

            {/* Параметри видимості та сортування */}
            <div className="form-wrapper-2-column">
                <div className="form-group">
                    <label htmlFor="sortOrder">Порядок сортування</label>
                    <CustomInput
                        id="sortOrder" name="sortOrder" type="number" label=""
                        value={formik.values.sortOrder}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="0"
                        error={formik.errors.sortOrder}
                        touched={formik.touched.sortOrder}
                    />
                </div>

                <div className="form-group checkbox-group">
                    <label htmlFor="isFeatured" className="checkbox-label">
                        <input
                            type="checkbox"
                            id="isFeatured"
                            name="isFeatured"
                            checked={formik.values.isFeatured}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                        <span>Показувати на головній сторінці</span>
                    </label>
                </div>
            </div>

            <div className="form-group checkbox-group">
                <label htmlFor="isActive" className="checkbox-label">
                    <input
                        type="checkbox"
                        id="isActive"
                        name="isActive"
                        checked={formik.values.isActive}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    <span>Активний учасник</span>
                </label>
            </div>
        </form>
    );
};

export default TeamForm;
